import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Folder, Tablet, Box, Tag, Layers, Archive, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping
const ICONS = {
  folder: Folder,
  tablet: Tablet,
  box: Box,
  tag: Tag,
  layers: Layers,
  archive: Archive,
};

export default function PublicCardListPage() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const res = await fetch(`${API}/public/cards`);
        if (!res.ok) throw new Error("Errore nel caricamento");
        const data = await res.json();
        setCards(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCards();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white/70">
          <p className="text-xl mb-2">Errore</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <img 
              src="/sdg-logo-transparent.png" 
              alt="Logo" 
              className="h-10 w-auto"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <h1 className="text-2xl font-bold text-white">Guide Assistenza</h1>
              <p className="text-sm text-white/60">Seleziona una categoria per vedere le guide disponibili</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {cards.length === 0 ? (
          <div className="text-center py-20">
            <Folder className="w-16 h-16 mx-auto text-white/30 mb-4" />
            <h2 className="text-xl font-semibold text-white/70 mb-2">Nessuna guida disponibile</h2>
            <p className="text-white/50">Le guide saranno disponibili a breve.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card) => {
              const IconComponent = ICONS[card.icon] || Folder;
              return (
                <Card
                  key={card.id}
                  className="group cursor-pointer bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
                  onClick={() => navigate(`/c/${card.slug}`)}
                  data-testid={`card-${card.slug}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                        style={{ backgroundColor: card.color + '25' }}
                      >
                        <IconComponent 
                          className="w-7 h-7" 
                          style={{ color: card.color }}
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-white truncate">
                            {card.name}
                          </h3>
                          <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-white/60 group-hover:translate-x-1 transition-all" />
                        </div>
                        
                        {card.description && (
                          <p className="text-sm text-white/50 line-clamp-2 mb-3">
                            {card.description}
                          </p>
                        )}
                        
                        <Badge 
                          variant="outline" 
                          className="text-xs border-white/20 text-white/60"
                        >
                          {card.guide_count} {card.guide_count === 1 ? 'guida' : 'guide'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-auto">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-white/40 text-sm">
          © {new Date().getFullYear()} Sistemi Digitali Group
        </div>
      </footer>
    </div>
  );
}
